import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Search, MessageSquare, RefreshCw, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import type { ClusterSummary, NextStepsData, TopicSummary } from '../../types/journeys';
import type { ClusterListItem } from '../../api/journeys';
import PathBreadcrumbs from './PathBreadcrumbs';
import NextStepsList from './NextStepsList';

interface TopicExplorerProps {
  clusters: ClusterListItem[];
  clustersLoading: boolean;
  nextStepsData: NextStepsData | null;
  nextStepsLoading: boolean;
  explorationPath: ClusterSummary[];
  suggestedTopics: TopicSummary[];
  onSelectTopic: (cluster: ClusterSummary) => void;
  onNavigateToPathIndex: (index: number) => void;
  onClearPath: () => void;
  onShowExamples: (fromClusterId: number, toClusterId: number) => void;
  selectedKey: string;
  isPathBased?: boolean; // True when showing results from a path query (2+ clusters)
}

export default function TopicExplorer({
  clusters,
  clustersLoading,
  nextStepsData,
  nextStepsLoading,
  explorationPath,
  suggestedTopics,
  onSelectTopic,
  onNavigateToPathIndex,
  onClearPath,
  onShowExamples,
  selectedKey,
  isPathBased = false,
}: TopicExplorerProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const formatNumber = (n: number) =>
    n >= 1_000_000
      ? `${(n / 1_000_000).toFixed(1)}M`
      : n >= 1_000
      ? `${(n / 1_000).toFixed(1)}K`
      : n.toString();

  // Filter clusters by selected key and search query
  const filteredClusters = useMemo(() => {
    return clusters
      .filter((c) => c.key === selectedKey)
      .filter((c) =>
        searchQuery
          ? c.label.toLowerCase().includes(searchQuery.toLowerCase())
          : true
      )
      .slice(0, 10);
  }, [clusters, selectedKey, searchQuery]);

  const currentCluster = explorationPath.length > 0
    ? explorationPath[explorationPath.length - 1]
    : null;

  const handleClusterClick = (cluster: ClusterListItem | ClusterSummary) => {
    onSelectTopic({
      id: cluster.id,
      label: cluster.label,
      key: cluster.key,
    });
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-800">Pick a Topic - See What's Next</h2>

      {/* Search and suggestions */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Search input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Search results or suggestions */}
            {searchQuery ? (
              <div className="space-y-2">
                {clustersLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredClusters.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No topics found matching "{searchQuery}"
                  </p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {filteredClusters.map((cluster) => (
                      <Button
                        key={cluster.id}
                        variant="outline"
                        className="justify-start h-auto py-2 px-3"
                        onClick={() => handleClusterClick(cluster)}
                      >
                        <div className="text-left">
                          <div className="font-medium text-sm truncate">
                            {cluster.label}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatNumber(cluster.size)} events
                          </div>
                        </div>
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Suggested topics when no search */
              <div>
                <div className="text-sm text-gray-500 mb-2">Suggested starting points:</div>
                <div className="flex flex-wrap gap-2">
                  {suggestedTopics.slice(0, 6).map((topic) => (
                    <Button
                      key={topic.cluster.id}
                      variant="outline"
                      size="sm"
                      onClick={() => handleClusterClick(topic.cluster)}
                    >
                      {topic.cluster.label}
                    </Button>
                  ))}
                  {suggestedTopics.length === 0 && !clustersLoading && (
                    <p className="text-sm text-gray-400">
                      No suggested topics available. Try searching above.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Path breadcrumbs */}
      <PathBreadcrumbs
        path={explorationPath}
        onNavigate={onNavigateToPathIndex}
        onClear={onClearPath}
      />

      {/* Next steps display */}
      {currentCluster && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {isPathBased ? (
                <>After this path...</>
              ) : (
                <>After "{currentCluster.label}"...</>
              )}
            </CardTitle>
            {nextStepsData && (
              <CardDescription>
                <TooltipProvider delayDuration={200}>
                  {isPathBased ? (
                    <span className="inline-flex items-center flex-wrap gap-y-1">
                      <strong>{formatNumber(nextStepsData.total_conversations_with_topic)}</strong>&nbsp;conversations followed this exact path
                      <span className="mx-2 text-muted-foreground">|</span>
                      <span className="inline-flex items-center gap-1">
                        <span className="text-green-600 font-medium">{nextStepsData.continuation_percentage}%</span> continued
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help transition-colors" />
                          </TooltipTrigger>
                          <TooltipContent 
                            side="top" 
                            className="max-w-[280px] bg-gray-900 text-gray-100 border-gray-800 px-3 py-2 text-xs leading-relaxed shadow-xl"
                          >
                            <p className="font-medium text-white mb-1">Continuation Rate</p>
                            <p>The percentage of conversations that had additional activity after reaching this point in the journey.</p>
                          </TooltipContent>
                        </Tooltip>
                        {nextStepsData.ended_conversation_count > 0 && (
                          <span className="text-gray-500 ml-1">· {formatNumber(nextStepsData.ended_conversation_count)} ended here</span>
                        )}
                      </span>
                      {nextStepsData.return_to_topic_count > 0 && (
                        <>
                          <span className="mx-2 text-muted-foreground">|</span>
                          <span className="inline-flex items-center gap-1">
                            {formatNumber(nextStepsData.return_to_topic_count)} returned
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help transition-colors" />
                              </TooltipTrigger>
                              <TooltipContent 
                                side="top" 
                                className="max-w-[280px] bg-gray-900 text-gray-100 border-gray-800 px-3 py-2 text-xs leading-relaxed shadow-xl"
                              >
                                <p className="font-medium text-white mb-1">Returned</p>
                                <p>Conversations that left this topic but circled back to it later in the same session—a "boomerang" pattern indicating revisited interest.</p>
                              </TooltipContent>
                            </Tooltip>
                          </span>
                        </>
                      )}
                    </span>
                  ) : (
                    <span className="inline-flex items-center flex-wrap gap-y-1">
                      {formatNumber(nextStepsData.total_conversations_with_topic)} total conversations with this topic
                      <span className="mx-2 text-muted-foreground">|</span>
                      <span className="inline-flex items-center gap-1">
                        <span className="text-green-600 font-medium">{nextStepsData.continuation_percentage}%</span> continued
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help transition-colors" />
                          </TooltipTrigger>
                          <TooltipContent 
                            side="top" 
                            className="max-w-[280px] bg-gray-900 text-gray-100 border-gray-800 px-3 py-2 text-xs leading-relaxed shadow-xl"
                          >
                            <p className="font-medium text-white mb-1">Continuation Rate</p>
                            <p>The percentage of conversations that had additional activity after this topic—users who didn't drop off here.</p>
                          </TooltipContent>
                        </Tooltip>
                        {nextStepsData.ended_conversation_count > 0 && (
                          <span className="text-gray-500 ml-1">· {formatNumber(nextStepsData.ended_conversation_count)} ended here</span>
                        )}
                      </span>
                      {nextStepsData.return_to_topic_count > 0 && (
                        <>
                          <span className="mx-2 text-muted-foreground">|</span>
                          <span className="inline-flex items-center gap-1">
                            {formatNumber(nextStepsData.return_to_topic_count)} returned
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help transition-colors" />
                              </TooltipTrigger>
                              <TooltipContent 
                                side="top" 
                                className="max-w-[280px] bg-gray-900 text-gray-100 border-gray-800 px-3 py-2 text-xs leading-relaxed shadow-xl"
                              >
                                <p className="font-medium text-white mb-1">Returned</p>
                                <p>Conversations that left this topic but circled back to it later in the same session—a "boomerang" pattern indicating revisited interest.</p>
                              </TooltipContent>
                            </Tooltip>
                          </span>
                        </>
                      )}
                    </span>
                  )}
                </TooltipProvider>
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <NextStepsList
              nextSteps={nextStepsData?.next_steps || []}
              onStepClick={(clusterId) => {
                const step = nextStepsData?.next_steps.find((s) => s.cluster.id === clusterId);
                if (step) {
                  handleClusterClick(step.cluster);
                }
              }}
              onShowExamples={onShowExamples}
              currentClusterId={currentCluster.id}
              loading={nextStepsLoading}
            />
          </CardContent>
        </Card>
      )}

      {/* Empty state when no topic selected */}
      {!currentCluster && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Select a topic above to explore conversation flows</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
